/**
 * Member Dashboard — single entry point after login.
 *
 * Sections (tabs on desktop, accordion on mobile):
 *   - Overview  : 4 summary cards + recent activity
 *   - Billing   : invoice table with PDF download (primary)
 *   - Subscription : current plan, upgrade/downgrade/cancel
 *   - Credits   : balance + purchase entry point + usage history
 *   - History   : conversion log (recent files)
 *   - Account   : profile edit + change password
 *
 * The Billing tab gets the most attention per the spec — users must be
 * able to retrieve and download invoices at any time without email.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Gauge,
  CreditCard,
  Lightning,
  Receipt,
  ArrowUpRight,
  ArrowSquareOut,
  DownloadSimple,
  CheckCircle,
  Clock,
  XCircle,
  WarningCircle,
  House,
  ClockCounterClockwise,
  UserCircle,
  SignOut,
  Bell,
  Trash,
  Lock,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  fetchBillingMe,
  fetchInvoices,
  cancelSubscription,
  changePlan,
  downloadInvoicePdf,
} from "../lib/billing";
import {
  updateProfile,
  changePassword,
  fetchNotifications,
  markNotificationsRead,
  deleteNotification,
} from "../lib/profile";
import { COUNTRIES } from "../lib/countries";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: House },
  { id: "billing", label: "Billing & Invoices", icon: Receipt },
  { id: "credits", label: "Conversions", icon: Lightning },
  { id: "history", label: "Conversion history", icon: ClockCounterClockwise },
  { id: "account", label: "Account", icon: UserCircle },
];

export default function Dashboard() {
  const { user, booted, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "overview";
  const [tab, setTabState] = useState(SECTIONS.some((s) => s.id === initialTab) ? initialTab : "overview");

  // Wrap the tab setter so we update the URL synchronously with the
  // state change — this avoids a useEffect-driven set, which trips the
  // `react-hooks/set-state-in-effect` lint rule.
  const setTab = (newTab) => {
    setTabState(newTab);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newTab === "overview") next.delete("tab");
      else next.set("tab", newTab);
      return next;
    }, { replace: true });
  };

  const [billing, setBilling] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [history, setHistory] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [me, inv, conv, n, use] = await Promise.all([
        fetchBillingMe(),
        fetchInvoices(),
        api.get("/conversions").then((r) => r.data).catch(() => []),
        fetchNotifications(25).catch(() => ({ items: [], unread: 0 })),
        api.get("/usage/me").then((r) => r.data).catch(() => null),
      ]);
      setBilling(me);
      setInvoices(inv);
      setHistory(Array.isArray(conv) ? conv : []);
      setNotifs(n);
      setUsage(use);
    } catch (e) {
      toast.error("Couldn't load some dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user !== false) refreshAll();
  }, [user]);

  if (!booted) return <DashLoading />;
  if (!user || user === false) {
    return <Navigate to="/login?next=/dashboard" replace />;
  }

  const sub = billing?.subscription;
  const latestInv = invoices[0] || null;

  return (
    <div className="max-w-[1440px] mx-auto" data-testid="member-dashboard">
      {/* Top bar */}
      <div className="border-b border-[var(--border)] px-6 md:px-10 py-8 md:py-12 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="micro-label flex items-center gap-2">
            <Gauge size={12} weight="bold" /> / Member dashboard
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-2">
            Welcome back, {user.name || user.email.split("@")[0]}.
          </h1>
          <p className="text-[var(--fg-2)] mt-3 max-w-xl">
            Your plan, credits, invoices and account — all in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsButton notifs={notifs} onChange={refreshAll} />
          <button
            onClick={async () => {
              await logout();
              navigate("/");
              toast.success("Signed out");
            }}
            className="btn-ghost"
            data-testid="dashboard-signout"
          >
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] min-h-[60vh]">
        {/* Sidebar nav */}
        <aside className="border-b lg:border-b-0 lg:border-r border-[var(--border)] bg-[var(--bg-2)] lg:py-8" data-testid="dashboard-nav">
          <nav className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible">
            {SECTIONS.map((s) => {
              const active = tab === s.id;
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setTab(s.id)}
                  className={`flex items-center gap-3 px-6 py-4 text-sm font-mono uppercase tracking-wider whitespace-nowrap border-l-2 transition-colors ${
                    active
                      ? "border-[var(--accent)] bg-white text-[var(--fg)] font-semibold"
                      : "border-transparent text-[var(--fg-2)] hover:bg-white/60 hover:text-[var(--fg)]"
                  }`}
                  data-testid={`dashboard-tab-${s.id}`}
                >
                  <Icon size={16} weight={active ? "bold" : "regular"} />
                  <span>{s.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main panel */}
        <main className="p-6 md:p-10">
          {loading && !billing ? (
            <DashLoading />
          ) : (
            <>
              {tab === "overview" && (
                <OverviewSection
                  user={user}
                  billing={billing}
                  usage={usage}
                  latestInv={latestInv}
                  history={history}
                  onJumpTo={setTab}
                />
              )}
              {tab === "billing" && (
                <BillingSection
                  invoices={invoices}
                  payments={billing?.payments || []}
                />
              )}
              {tab === "credits" && (
                <CreditsSection
                  billing={billing}
                  usage={usage}
                  history={history}
                />
              )}
              {tab === "history" && <HistorySection history={history} />}
              {tab === "account" && (
                <AccountSection
                  user={user}
                  onRefresh={async () => {
                    await refresh();
                    await refreshAll();
                  }}
                />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Overview                                                    */
/* ============================================================ */

function OverviewSection({ user, billing, usage, latestInv, history, onJumpTo }) {
  const unlimited = usage?.unlimited;
  const freeC = usage?.free_credits ?? 0;
  const paidC = usage?.paid_credits ?? 0;
  const totalC = usage?.total_credits ?? 0;
  const totalConv = history.length;

  return (
    <div data-testid="overview-section">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          label="Conversions left"
          value={unlimited ? "∞" : String(totalC)}
          sub={unlimited ? "ADMIN · UNLIMITED" : "READY TO USE"}
          accent="ok"
          onClick={() => onJumpTo("credits")}
          testid="card-credits"
        />
        <Card
          label="Free this month"
          value={unlimited ? "∞" : String(freeC)}
          sub="RESETS ON THE 1ST"
          accent="accent"
          onClick={() => onJumpTo("credits")}
          testid="card-free-credits"
        />
        <Card
          label="Paid conversions"
          value={unlimited ? "∞" : String(paidC)}
          sub="NEVER EXPIRE"
          accent=""
          onClick={() => onJumpTo("credits")}
          testid="card-paid-credits"
        />
        <Card
          label="Total conversions"
          value={String(totalConv)}
          sub="ALL TIME"
          accent=""
          onClick={() => onJumpTo("history")}
          testid="card-conversions"
        />
      </div>

      {/* Quick actions */}
      <div className="mt-10">
        <div className="micro-label">Quick actions</div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Link to="/convert" className="hairline bg-white p-5 hover:bg-[var(--bg-2)] transition-colors" data-testid="action-convert">
            <Lightning size={20} weight="bold" className="text-[var(--accent)]" />
            <div className="mt-2 font-display font-bold">Start a conversion</div>
            <div className="text-xs text-[var(--fg-2)] mt-1">Open the converter</div>
          </Link>
          <Link to="/pricing" className="hairline bg-white p-5 hover:bg-[var(--bg-2)] transition-colors" data-testid="action-buy">
            <CreditCard size={20} weight="bold" />
            <div className="mt-2 font-display font-bold">Buy a conversion pack</div>
            <div className="text-xs text-[var(--fg-2)] mt-1">One-time · credits never expire</div>
          </Link>
          <button onClick={() => onJumpTo("account")} className="hairline bg-white p-5 hover:bg-[var(--bg-2)] transition-colors text-left" data-testid="action-account">
            <UserCircle size={20} weight="bold" />
            <div className="mt-2 font-display font-bold">Edit account</div>
            <div className="text-xs text-[var(--fg-2)] mt-1">Name, country, password</div>
          </button>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, sub, onClick, accent, testid }) {
  const dotCls = accent === "ok" ? "ok" : accent === "warn" ? "warn" : accent === "accent" ? "" : "";
  return (
    <button
      onClick={onClick}
      className="text-left hairline bg-white p-5 hover:border-[var(--fg)] transition-colors group"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="micro-label">{label}</div>
        <ArrowUpRight size={14} weight="bold" className="text-[var(--fg-2)] group-hover:text-[var(--accent)] transition-colors" />
      </div>
      <div className="mt-3 font-display font-black text-3xl truncate">{value}</div>
      <div className="text-[10px] font-mono text-[var(--fg-2)] tracking-wider mt-1 flex items-center gap-1.5">
        {dotCls && <span className={`status-dot ${dotCls}`}></span>}
        {sub}
      </div>
    </button>
  );
}

/* ============================================================ */
/*  Billing & Invoices                                          */
/* ============================================================ */

function BillingSection({ invoices, payments }) {
  // Merge subscription invoices (kind=invoice) + credit-pack payments
  // (kind=credit_pack) into a single timeline. `fetchInvoices` already
  // returns both; we keep `payments` as a fallback in case the backend
  // ever splits them.
  const items = useMemo(() => {
    const list = Array.isArray(invoices) ? invoices : [];
    if (list.length > 0) return list;
    return (payments || []).map((p) => ({ ...p, kind: "credit_pack" }));
  }, [invoices, payments]);

  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = async (it) => {
    if (!it?.id) return;
    setDownloadingId(it.id);
    try {
      await downloadInvoicePdf(it.id);
      toast.success("Invoice downloaded");
    } catch (e) {
      toast.error("Could not download invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div data-testid="billing-section">
      <SectionHeader
        eyebrow="Billing & Invoices"
        title="Your payment history"
        sub="All transactions, receipts and PDF invoices — available anytime, no email required."
      />

      {items.length === 0 ? (
        <EmptyState
          icon={<Receipt size={28} weight="regular" className="text-[var(--fg-2)]" />}
          message="No invoices yet. They'll appear here after your first successful payment."
          testid="invoices-empty"
        />
      ) : (
        <div className="hairline bg-white overflow-x-auto" data-testid="invoices-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                <th className="p-3 text-left micro-label font-normal">Invoice #</th>
                <th className="p-3 text-left micro-label font-normal">Date</th>
                <th className="p-3 text-left micro-label font-normal">Description</th>
                <th className="p-3 text-left micro-label font-normal">Method</th>
                <th className="p-3 text-right micro-label font-normal">Amount</th>
                <th className="p-3 text-left micro-label font-normal">Status</th>
                <th className="p-3 text-right micro-label font-normal">PDF</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-2)]/50">
                  <td className="p-3 font-mono text-xs">{(it.id || "").slice(0, 14)}</td>
                  <td className="p-3 font-mono text-xs">
                    {it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3">
                    {it.kind === "invoice" || it.plan_id ? (
                      <>
                        <div>{planName(it.plan_id) || "Subscription"}</div>
                        <div className="text-[10px] text-[var(--fg-2)] font-mono uppercase tracking-wider">Subscription</div>
                      </>
                    ) : (
                      <>
                        <div>{packName(it.pack_id) || "Credit pack"}</div>
                        <div className="text-[10px] text-[var(--fg-2)] font-mono uppercase tracking-wider">One-off</div>
                      </>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs">
                    {it.provider === "peach" ? "Card" : (it.provider || "—").toUpperCase()}
                  </td>
                  <td className="p-3 text-right font-mono">
                    {formatMoney(it.amount, it.currency)}
                  </td>
                  <td className="p-3">
                    <StatusChip status={it.status} />
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => handleDownload(it)}
                      disabled={downloadingId === it.id || (it.status !== "paid" && it.status !== "completed")}
                      className="btn-ghost px-2.5 py-1 text-[10px] inline-flex items-center gap-1.5 disabled:opacity-40"
                      data-testid={`download-invoice-${it.id}`}
                      title={
                        it.status === "paid" || it.status === "completed"
                          ? "Download PDF invoice"
                          : "PDF available after payment is complete"
                      }
                    >
                      <DownloadSimple size={12} weight="bold" />
                      {downloadingId === it.id ? "..." : "PDF"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  Subscription                                                */
/* ============================================================ */

function SubscriptionSection({ sub, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const onCancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current period?")) return;
    setBusy(true);
    try {
      await cancelSubscription();
      toast.success("Subscription canceled — access continues until period end");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onChange = async (newPlanId) => {
    setBusy(true);
    try {
      await changePlan(newPlanId);
      toast.success("Plan updated");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="subscription-section">
      <SectionHeader
        eyebrow="Subscription"
        title="Your current plan"
        sub="Upgrade, downgrade, or cancel — all from here."
      />

      {!sub ? (
        <div className="hairline bg-white p-8" data-testid="sub-empty">
          <p className="text-[var(--fg-2)]">
            You&apos;re on the <strong>Free</strong> plan. Upgrade for more conversions, bulk uploads and priority queue.
          </p>
          <Link to="/pricing" className="btn-accent mt-5 inline-flex items-center gap-2">
            See plans <ArrowSquareOut size={14} weight="bold" />
          </Link>
        </div>
      ) : (
        <div className="hairline bg-white p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display font-black text-4xl">{planName(sub.plan_id)}</div>
              <div className="mt-2 flex items-center gap-2">
                <StatusChip status={sub.status} />
                {sub.is_mock && (
                  <span className="chip border-[var(--accent)] text-[var(--accent)]">MOCK</span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="micro-label">Next renewal</div>
              <div className="font-display font-bold text-lg mt-1">
                {sub.current_period_end
                  ? new Date(sub.current_period_end).toLocaleDateString()
                  : "—"}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
            <Metric label="Amount" value={formatMoney(sub.amount, sub.currency)} />
            <Metric label="Provider" value={(sub.provider || "—").toUpperCase()} />
            <Metric label="Renewal" value={sub.status === "active" ? "Auto" : "—"} />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {sub.status === "active" && sub.plan_id === "standard" && (
              <>
                <button onClick={() => onChange("premium")} disabled={busy} className="btn-accent" data-testid="btn-upgrade-premium">
                  Upgrade to Premium <ArrowUpRight size={14} weight="bold" />
                </button>
                <button onClick={() => onChange("pro")} disabled={busy} className="btn-primary" data-testid="btn-upgrade-pro">
                  Upgrade to Pro
                </button>
              </>
            )}
            {sub.status === "active" && sub.plan_id === "premium" && (
              <>
                <button onClick={() => onChange("pro")} disabled={busy} className="btn-accent" data-testid="btn-upgrade-pro">
                  Upgrade to Pro <ArrowUpRight size={14} weight="bold" />
                </button>
                <button onClick={() => onChange("standard")} disabled={busy} className="btn-ghost" data-testid="btn-downgrade-standard">
                  Downgrade to Standard
                </button>
              </>
            )}
            {sub.status === "active" && sub.plan_id === "pro" && (
              <button onClick={() => onChange("premium")} disabled={busy} className="btn-ghost" data-testid="btn-downgrade-premium">
                Downgrade to Premium
              </button>
            )}
            {sub.status === "active" && (
              <button onClick={onCancel} disabled={busy} className="btn-ghost" data-testid="btn-cancel-sub">
                <XCircle size={14} weight="bold" /> Cancel subscription
              </button>
            )}
            {sub.status === "canceled" && (
              <Link to="/pricing" className="btn-accent" data-testid="btn-reactivate">
                Reactivate <ArrowUpRight size={14} weight="bold" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  Credits                                                     */
/* ============================================================ */

function CreditsSection({ billing, usage, history }) {
  const recent = useMemo(() => {
    return (history || [])
      .filter((h) => Number(h.credits_used || h.credits || 0) > 0)
      .slice(0, 20);
  }, [history]);

  const unlimited = usage?.unlimited;
  const freeC = usage?.free_credits ?? 0;
  const paidC = usage?.paid_credits ?? 0;
  const totalC = usage?.total_credits ?? 0;

  return (
    <div data-testid="credits-section">
      <SectionHeader
        eyebrow="Conversions"
        title="Your conversion balance"
        sub="1 credit = 1 conversion. Free conversions reset monthly; paid never expire."
      />

      <div className="hairline bg-white p-6 md:p-8">
        {unlimited ? (
          <div>
            <div className="micro-label">Admin account</div>
            <div className="font-display font-black text-5xl mt-2 text-[var(--accent)]">∞</div>
            <div className="text-xs font-mono text-[var(--fg-2)] tracking-wider mt-1">UNLIMITED CONVERSIONS</div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="micro-label">You have</div>
              <div className="font-display font-black text-6xl mt-1 text-[var(--accent)]" data-testid="credits-total">
                {totalC}
              </div>
              <div className="text-xs font-mono text-[var(--fg-2)] tracking-wider mt-1">
                CONVERSION{totalC === 1 ? "" : "S"} REMAINING
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 border-t border-[var(--border)] pt-6">
              <div data-testid="credits-free">
                <div className="micro-label">Free this month</div>
                <div className="font-display font-black text-4xl mt-2">{freeC}</div>
                <div className="text-xs font-mono text-[var(--fg-2)] tracking-wider mt-1">
                  RESETS ON THE 1ST · NO ROLL-OVER
                </div>
              </div>
              <div data-testid="credits-paid">
                <div className="micro-label">Paid conversions</div>
                <div className="font-display font-black text-4xl mt-2">{paidC}</div>
                <div className="text-xs font-mono text-[var(--fg-2)] tracking-wider mt-1">
                  NEVER EXPIRE
                </div>
              </div>
            </div>
          </>
        )}
        <Link to="/pricing" className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="btn-buy-credits">
          <Lightning size={14} weight="bold" /> Buy a conversion pack
        </Link>
      </div>

      <div className="mt-8">
        <div className="micro-label">Recent usage</div>
        {recent.length === 0 ? (
          <EmptyState
            icon={<Lightning size={28} weight="regular" className="text-[var(--fg-2)]" />}
            message="No conversions yet. Run a conversion to see it logged here."
            testid="credits-usage-empty"
          />
        ) : (
          <div className="hairline bg-white mt-4 overflow-x-auto" data-testid="credits-usage-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                  <th className="p-3 text-left micro-label font-normal">Date</th>
                  <th className="p-3 text-left micro-label font-normal">File</th>
                  <th className="p-3 text-right micro-label font-normal">Conversions</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((h, i) => (
                  <tr key={h.id || i} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="p-3 font-mono text-xs">
                      {h.created_at ? new Date(h.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 truncate max-w-xs">{h.input_name || h.name || "—"}</td>
                    <td className="p-3 text-right font-mono">1</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Conversion history                                          */
/* ============================================================ */

function HistorySection({ history }) {
  return (
    <div data-testid="history-section">
      <SectionHeader eyebrow="Conversion history" title="Files you've processed" sub="Newest first. Download or remove from the History page." />
      {(!history || history.length === 0) ? (
        <EmptyState
          icon={<ClockCounterClockwise size={28} weight="regular" className="text-[var(--fg-2)]" />}
          message="No conversions yet — go run your first."
          testid="history-empty"
        />
      ) : (
        <div className="hairline bg-white overflow-x-auto" data-testid="history-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-2)]">
                <th className="p-3 text-left micro-label font-normal">Date</th>
                <th className="p-3 text-left micro-label font-normal">File name</th>
                <th className="p-3 text-left micro-label font-normal">Type</th>
                <th className="p-3 text-right micro-label font-normal">Credits</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 50).map((h, i) => (
                <tr key={h.id || i} className="border-b border-[var(--border)] last:border-b-0">
                  <td className="p-3 font-mono text-xs">
                    {h.created_at ? new Date(h.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3 truncate max-w-md">{h.input_name || h.name || "—"}</td>
                  <td className="p-3 text-[var(--fg-2)] uppercase tracking-wider text-xs font-mono">
                    {h.kind || h.mode || "convert"}
                  </td>
                  <td className="p-3 text-right font-mono">{h.credits_used || h.credits || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/*  Account                                                     */
/* ============================================================ */

function AccountSection({ user, onRefresh }) {
  const [name, setName] = useState(user.name || "");
  const [country, setCountry] = useState(user.country || "ZA");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile({ name, country });
      toast.success("Profile updated");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setSavingPw(true);
    try {
      await changePassword(currentPw, newPw);
      toast.success("Password updated");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div data-testid="account-section">
      <SectionHeader eyebrow="Account" title="Profile & security" sub="Update your details or change your password." />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile */}
        <form onSubmit={saveProfile} className="hairline bg-white p-6" data-testid="profile-form">
          <div className="micro-label">Profile</div>
          <h3 className="font-display font-bold text-xl mt-1">Your details</h3>

          <label className="block mt-6">
            <span className="micro-label">Email (read-only)</span>
            <input
              type="email"
              value={user.email}
              disabled
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-[var(--bg-2)] font-mono text-sm text-[var(--fg-2)]"
              data-testid="profile-email"
            />
          </label>

          <label className="block mt-4">
            <span className="micro-label">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              maxLength={80}
              data-testid="profile-name"
            />
          </label>

          <label className="block mt-4">
            <span className="micro-label">Country</span>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none appearance-none cursor-pointer"
              data-testid="profile-country"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </label>

          <div className="mt-5 text-[10px] font-mono text-[var(--fg-2)] tracking-wider grid grid-cols-2 gap-3">
            <div>
              <div className="micro-label">Member since</div>
              <div className="mt-1">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</div>
            </div>
            <div>
              <div className="micro-label">Currency</div>
              <div className="mt-1">ZAR (Rand)</div>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingProfile}
            className="btn-accent mt-6 inline-flex items-center gap-2"
            data-testid="btn-save-profile"
          >
            {savingProfile ? "Saving…" : "Save profile"}
          </button>
        </form>

        {/* Password */}
        <form onSubmit={savePassword} className="hairline bg-white p-6" data-testid="password-form">
          <div className="micro-label flex items-center gap-2">
            <Lock size={12} weight="bold" /> Security
          </div>
          <h3 className="font-display font-bold text-xl mt-1">Change password</h3>

          <label className="block mt-6">
            <span className="micro-label">Current password</span>
            <input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="password-current"
            />
          </label>
          <label className="block mt-4">
            <span className="micro-label">New password (8+ chars)</span>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="password-new"
            />
          </label>
          <label className="block mt-4">
            <span className="micro-label">Confirm new password</span>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              minLength={8}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="password-confirm"
            />
          </label>

          <button
            type="submit"
            disabled={savingPw}
            className="btn-primary mt-6 inline-flex items-center gap-2"
            data-testid="btn-save-password"
          >
            {savingPw ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Notifications                                               */
/* ============================================================ */

function NotificationsButton({ notifs, onChange }) {
  const [open, setOpen] = useState(false);

  const markAllRead = async () => {
    try {
      await markNotificationsRead([]);
      await onChange();
    } catch (_) { /* noop */ }
  };

  const remove = async (id) => {
    try {
      await deleteNotification(id);
      await onChange();
    } catch (_) { /* noop */ }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost relative"
        data-testid="dashboard-notifications-btn"
        aria-label="Notifications"
      >
        <Bell size={16} weight="bold" />
        {notifs.unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[var(--accent)] text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
            {notifs.unread > 9 ? "9+" : notifs.unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[40]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 z-[50] w-[360px] max-h-[480px] overflow-y-auto bg-white border border-[var(--border)] shadow-xl" data-testid="notifications-panel">
            <div className="px-4 py-3 border-b border-[var(--border)] sticky top-0 bg-white flex items-center justify-between">
              <div className="font-display font-bold">Notifications</div>
              {notifs.unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] font-mono uppercase tracking-wider text-[var(--accent)] hover:underline"
                  data-testid="btn-mark-all-read"
                >
                  Mark all read
                </button>
              )}
            </div>
            {notifs.items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-[var(--fg-2)]">
                <Bell size={24} className="mx-auto mb-2 opacity-50" />
                No notifications yet.
              </div>
            ) : (
              <ul>
                {notifs.items.map((n) => (
                  <li
                    key={n.id}
                    className={`px-4 py-3 border-b border-[var(--border)] last:border-b-0 ${n.read ? "" : "bg-[var(--bg-2)]"}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <NotificationIcon kind={n.kind} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{n.title}</div>
                        <div className="text-xs text-[var(--fg-2)] mt-0.5 line-clamp-2">{n.body}</div>
                        <div className="text-[10px] font-mono text-[var(--fg-2)] tracking-wider mt-1">
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      <button
                        onClick={() => remove(n.id)}
                        className="text-[var(--fg-2)] hover:text-[var(--accent)]"
                        aria-label="Delete notification"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function NotificationIcon({ kind }) {
  const map = {
    payment_success: { I: CheckCircle, cls: "text-[var(--success)]" },
    subscription_renewed: { I: CheckCircle, cls: "text-[var(--success)]" },
    payment_failed: { I: WarningCircle, cls: "text-red-600" },
    subscription_canceled: { I: XCircle, cls: "text-[var(--fg-2)]" },
    credit_purchase: { I: Lightning, cls: "text-[var(--accent)]" },
    system: { I: Bell, cls: "text-[var(--fg-2)]" },
  };
  const { I, cls } = map[kind] || map.system;
  return <I size={16} weight="bold" className={cls} />;
}

/* ============================================================ */
/*  Helpers                                                     */
/* ============================================================ */

function SectionHeader({ eyebrow, title, sub }) {
  return (
    <div className="mb-6 md:mb-8">
      <div className="micro-label">{eyebrow}</div>
      <h2 className="font-display font-black text-2xl md:text-3xl mt-1">{title}</h2>
      {sub && <p className="text-[var(--fg-2)] text-sm mt-2 max-w-2xl">{sub}</p>}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="micro-label">{label}</div>
      <div className="font-display font-bold text-lg mt-1">{value}</div>
    </div>
  );
}

function EmptyState({ icon, message, testid }) {
  return (
    <div className="hairline bg-white p-12 text-center" data-testid={testid}>
      <div className="mx-auto mb-3">{icon}</div>
      <p className="text-[var(--fg-2)]">{message}</p>
    </div>
  );
}

function StatusChip({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "paid" || s === "completed" || s === "active")
    return (
      <span className="chip border-[var(--success)] text-[var(--success)]">
        <CheckCircle size={11} weight="fill" />
        {s.toUpperCase()}
      </span>
    );
  if (s === "pending" || s === "processing")
    return (
      <span className="chip border-[var(--warning)] text-[var(--warning)]">
        <Clock size={11} weight="fill" />
        {s.toUpperCase()}
      </span>
    );
  if (s === "failed")
    return (
      <span className="chip border-red-600 text-red-600">
        <XCircle size={11} weight="fill" />
        {s.toUpperCase()}
      </span>
    );
  return (
    <span className="chip">
      <Clock size={11} weight="fill" />
      {(status || "—").toUpperCase()}
    </span>
  );
}

function DashLoading() {
  return (
    <div className="py-32 text-center text-[var(--fg-2)] font-mono text-xs tracking-widest">
      LOADING DASHBOARD…
    </div>
  );
}

function planName(id) {
  return (
    {
      standard: "Standard",
      premium: "Premium",
      pro: "Pro",
      pro_annual: "Pro Annual",
      creator: "Creator",
      free: "Free",
    }[id] || id || "Free"
  );
}

function packName(id) {
  return (
    {
      pack_10: "10-Pack",
      pack_50: "50-Pack",
      pack_100: "100-Pack",
      pack_500: "500-Pack",
    }[id] || id || "Credit pack"
  );
}

function formatMoney(amount, _currency) {
  // The whole product is ZAR-only — render every monetary value as Rands
  // regardless of what currency the database row was originally tagged
  // with (legacy USD imports, mock data, etc.). Underscored param kept
  // so call sites don't have to change.
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `R ${Number(amount).toFixed(2)}`;
  }
}
